import React from 'react';
import _ from 'lodash';
import pivot from 'data-pivot';
import { Chart } from 'react-chartjs-2';
import md5 from 'md5';

import { assMatch } from '../base/utils/assert';
import C from '../C';
import ServerIO from '../plumbing/ServerIO';
import DataStore from '../base/plumbing/DataStore';
import Misc from '../base/components/Misc';
import SimpleTable from '../base/components/SimpleTable';
import ActionMan from '../plumbing/ActionMan';
import { displayValue, DonationDataDisplay, formatCharityData, typeFromParam, pastCalendarDate, sortDonationsByValue } from './CommonTrafficReport';


const hasData = (data) => {
	return (
		Object.values(data).some(element => {
			if(!_.isEmpty(element)) return true;
			return false;
		})
	);
};

function PubTrafficReport({q}) {
	let path = DataStore.getValue(['location', 'path']);
	let pubId = path[1];

	//Fetch publisher data
	const end = null;
	const byHostOrAd = 'pub';
	// Start of presently selected time range
	const start = pastCalendarSwitch(DataStore.getValue(['location', 'params', 'timeline'])).toISOString();
	let data = dataFetchWrapper({q: ('pub:' + pubId), start , end, byHostOrAd});
	
	const {
		moneyFromHost,
		donationsdataByCharity,
		evthostdata,
		readableAdvertStats,
	} = data;
	
	if (! data || ! data.evthostdata || ! data.readableAdvertStats) {
		return (
			<div className="PubTrafficReport">
				<h3>Fetching your data...</h3>
				<Misc.Loading />
			</div>
		);
	}

	// No data
	if( !hasData(data) ) {
		return (
			<div className="PubTrafficReport">
				<h3>Nothing to report</h3>
				<p>
					No data is available for the publisher and time-range specified. 
					If you can't see the adunit on your website, please 
					<a href="mailto:publishers@good-loop.com">contact support</a> for help.
				</p>
			</div>
		);
	}

	// Raw values used to calculate figures used in final NewDataCard output
	const dataCardValues= {
		requests : (evthostdata.req&&evthostdata.req[q]? evthostdata.req[q] : 0),
		render: (evthostdata.render&&evthostdata.render[q]? evthostdata.render[q] : 0),
		visible : (evthostdata.visible&&evthostdata.visible[q]? evthostdata.visible[q] : 0),
		opens : (evthostdata.open&&evthostdata.open[q]? evthostdata.open[q] : 0) + (evthostdata.clicktolandingpage&&evthostdata.clicktolandingpage[q]? evthostdata.clicktolandingpage[q] : 0),
		complete : (evthostdata.minview&&evthostdata.minview[q]? evthostdata.minview[q] : 0),
		moneyFromHost: (moneyFromHost || 0)
	};

	return (
		<div>
			{/* <Misc.RadioGroup path={['location', 'params']} prop="timeline" headers={['lifetime', 'month', 'week', 'day']} noDefault /> */}
			<Misc.Tabs className="btn-group container timeline-buttons" liClassName="btn-primary btn-lg col-md-3" path={['location', 'params', 'timeline']}>
				<span option="Lifetime" style={{display: 'none'}} />
				<span option="Past month" style={{display: 'none'}} />
				<span option="Past week" style={{display: 'none'}} />
				<span option="Yesterday" style={{display: 'none'}} />
			</Misc.Tabs>
			<div className="container well-lg">
				<div className="col-md-6 well-lg" style={{textAlign: 'center'}}>
					<NewDataCard
						header="Impressions"
						datum={dataCardValues.render}//Being careful not to divide by zero
						additionalInformation={"Number of times that a Good-Loop advert appeared to one of your site's viewers."}
						percentage={dataCardValues.visible/dataCardValues.render}
					/>
					<h4>{`${parseFloat(((dataCardValues.visible/dataCardValues.render) * 100).toFixed(2)) || 0}% visible`}</h4>
				</div>
				<div className="col-md-6 well-lg" style={{textAlign: 'center'}}>
					<NewDataCard
						header="Video opens"
						datum={dataCardValues.opens}//parseFloat() gets rid of any trailing zeros
						additionalInformation={"Number of people who have clicked on a Good-Loop advert. A 'completed view' is counted when the user has watched the video for 15 seconds or more."}
						percentage={dataCardValues.complete/dataCardValues.opens}
					/>
					<h4>{`${parseFloat(((dataCardValues.complete/dataCardValues.opens) * 100).toFixed(2)) || 0}% completed views`}</h4>
				</div>
				<div className="col-md-6 well-lg" style={{textAlign: 'center'}}>
					<NewDataCard header="Total earnings">
						<div className="large-text">
							<Misc.Money amount={dataCardValues.moneyFromHost} />
						</div>
					</NewDataCard>
				</div>
				<div className="col-md-6 well-lg" style={{textAlign: 'center'}}>
					<NewDataCard header="Total donations">
						<div className="large-text">
							<Misc.Money amount={donationsdataByCharity && donationsdataByCharity.total ? donationsdataByCharity.total : 0} />
						</div>
					</NewDataCard>
				</div>
			</div>
			<Misc.CardAccordion>
				<Misc.Card title="Donations">
					{ donationsdataByCharity && donationsdataByCharity.total
						? <div>
							<DonationDataDisplay data={donationsdataByCharity} />
							<PieChart data={donationsdataByCharity} />
						</div>
						: "It looks like none of your users have made a donation yet."
					}
				</Misc.Card>
				<Misc.Card title="Requests by advertiser">
					<BreakdownWidget data={readableAdvertStats} param="Advertiser" />
				</Misc.Card>
			</Misc.CardAccordion>
		</div>
	);
}


/**
 * DISPLAY COMPONENTS
 * */

/**
* 
* @param {data: key -> Number}
* @param {param: String} The breakdown-by parameter, e.g. "list by publisher".
*/
function BreakdownWidget({data, param}) {
	//assMatch(param, String);
	if ( ! data) return null;

	let type = typeFromParam(param);
	let columns = [
		{
			Header: type || param,
			accessor: 'key',
			Cell: k => {
				let nk = k;
				let item = type && DataStore.getValue('data', type, k);
				if (item) nk = item.name || k;
				return nk;
			}
		},
		{
			Header: 'Amount',
			accessor: 'value',
			Cell: displayValue
		}
	];
	return <div className="well-lg"><SimpleTable columns={columns} dataObject={data} addTotalRow csv /></div>;
}


/**generate a react chart 2 pie chart */
function PieChart({data}) {
	if(!data || _.isEmpty(data) || !data.by_cid) return null;
	const {by_cid} = data;
	const formattedCharityData = formatCharityData(by_cid);
	const sorted = sortDonationsByValue(formattedCharityData);

	//taken from 'kelly's colours', supposed to be 22 mutually contrasting colours
	const backgroundColor = [
		'#F3C300', '#875692', '#F38400', '#8DB600', '#654522', '#E25822', '#2B3D26', '#A1CAF1',
		'#BE0032', '#C2B280', '#848482', '#008856', '#E68FAC', '#0067A5', '#F99379', '#604E97',
		'#F6A600', '#B3446C', '#DCD300', '#882D17'
	];
	//restrict to twelve most chosen charities
	//object comes pre-sorted, so can simply use slice.
	const labels = Object.keys(sorted).slice(0, 12);
	// only want donation value, not other parameters
	const dataArray = Object.values(sorted).slice(0, 12).map(charity => charity.donations.value);
	let total = (_.isArray(dataArray) && dataArray.length > 0) ? dataArray.reduce((t, currentInt) => t += currentInt*1, 0) : -1;

	const datasets = [{
		data: dataArray,
		backgroundColor,
		hoverBackgroundColor: backgroundColor
	}];

	const options = {
		animation: false,
		tooltips: {
			callbacks: {
				afterLabel: ({index}) => {
					return displayValue((dataArray[index]/total) * 100) + '%';
				}
			}
		}
	};

	return <Chart data={{labels, datasets}} options={options} type="pie" />;
}

/**Updated DataCard. Better able to handle new aesthetic requested by Brittani/Amy
* @param additionalInformation tooltip information for the given card
*/
function NewDataCard({header, children, datum, additionalInformation, percentage=1}) {
	if(typeof header === 'undefined' || (typeof datum === 'undefined' && typeof children === 'undefined')) {
		console.warn("PubTrafficReport, NewDataCard -- Value provided for header or datum is undefined");
		return null;
	}
	return (
		<div>
			<h3 className="large-text" style={{display: "inline-block"}}>{header}</h3>
			{	
				additionalInformation 
				&& <img src="img/info-icon.svg" className="info-icon" alt="info-icon" title={additionalInformation} />
			}
			<div className="circular-container">
				<RadialProgressBar percentage={percentage} />
				{children || <p className="large-text">{displayValue(datum)}</p>}
			</div>
		</div>
	);
}

/**
* 
* @param percentage should be a float (0.125 rather than 12.5%)
TODO: Move this to Misc? Would need styling in base.
*/
function RadialProgressBar({percentage}) {
	const r = 105;
	const xFinal = r * Math.sin(2*Math.PI*percentage);
	const yFinal = r * Math.cos(2*Math.PI*percentage);
	const largeAngle = percentage > 0.5 ? 1 : 0;
	const offset = {x: 3, y:3};
	const style = {fillOpacity:"0", stroke:"#9d130f", strokeWidth:"6"};
	const pathLength = 2 * Math.PI * r * percentage;//Used to animate circle

	//Arc below can't handle drawing 2*PI. Simplest solution: just use an ellipse
	if(percentage >= 1) {
		return (
			<svg xmlns="http://www.w3.org/2000/svg" width={(r * 2) + 6} height={(r * 2) + 6} className="radial-progress-bar">
				<ellipse style={style} cx={r+offset.x} cy={r+offset.y} rx={r} ry={r} />
			</svg>
		);
	}

	//Curve drawn using svg "a" (arc)
	//Explanation of arguments available at https://css-tricks.com/svg-path-syntax-illustrated-guide/
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width={(r * 2) + 6} height={(r * 2) + 6} className="radial-progress-bar">
			{/* <ellipse style={{fillOpacity:"0", stroke:"#9d130f", strokeWidth:"5"}} cx="112" cy="112" rx="110" ry="110" /> */}
			<ellipse style={{...style, stroke:"#c1c1c1"}} cx={r + offset.x} cy={r + offset.y} rx={r} ry={r} />
			<path
				strokeDasharray={`${pathLength} ${pathLength}`}
				style={style}
				d={`
					M ${r + offset.x}, ${offset.y}
					A ${r},${r} 0 ${largeAngle},1 ${xFinal + r + offset.x},${-yFinal + r + offset.y}`}
			>
				<animate attributeType="XML" attributeName="stroke-dashoffset" from={pathLength} to="0" dur={(percentage*1.5) + "s"} />
			</path>
		</svg>
	);
}

/** convenience method for reading in URL params
returns Date associated with past date string given
*/
const pastCalendarSwitch = (v) => {
	switch (v) {
	case 'Past month':
		return pastCalendarDate(0, 1);
	case 'Past week':
		return pastCalendarDate(7, 0);
	case 'Yesterday':
		return pastCalendarDate(1, 0);
	default:
		return new Date(C.defaultStartDateString);
	}
};

/**
 * DATA RETRIEVAL
*/

/**TODO add ability to restrict fetch request to only the data that you want
 * TODO break this up and put fetch requests closer to where they're actually needed
*/
const dataFetchWrapper = ({q, start, end, byHostOrAd}) => {
	// paths for storing data
	let dspec = md5(JSON.stringify({q, start, end, byHostOrAd}));
	const glpath = ['widget', 'PubTrafficReport', 'gl', dspec];
	const moneypath = ['widget', 'PubTrafficReport', 'money', dspec];
	const donationspath = ['widget', 'PubTrafficReport', 'donations', dspec];
	const adpath = ['widget', 'PubTrafficReport', 'vert', md5(JSON.stringify({q, start, end, byHostOrAd: 'vert'}))];
	const params = {q, start, end, dataspace: 'gl'};
	assMatch(byHostOrAd, String);

	let pgldata = DataStore.fetch(glpath, () => {
		// Where does ad activity data go??
		// dataspaces: trk for evt.pxl, goodloop for evt.viewable, adview, click, close, open, opened
		// money for spending
		// see https://github.com/good-loop/doc/wiki/Canonical-Terminology-for-Logging-Good-Loop-Events
		let breakdowns = ['evt/' + byHostOrAd, byHostOrAd];
		return ServerIO.getDataLogData({...params, breakdowns, name:'all-events',dataspace:'gl'})
			.then(res => res.cargo);
	});
	const gldata = pgldata.value;

	// Should be in form {by_vert:{...}, stats: {...}, total:{...}}
	let pvMoneyData = DataStore.fetch(moneypath, () => {
		return ServerIO.getRevenueData({q, breakdown: 'pub', start, end});
	});

	const moneyFromHost = pvMoneyData && pvMoneyData.value && pvMoneyData.value.total && pvMoneyData.value.total.value ? pvMoneyData.value.total.value : null;

	// donations
	let pvDonationsData = DataStore.fetch(donationspath, () => {
		return ServerIO.getDonationsData({q, start, end})
			.then(res => res.data);
	});
	let donationsdataByCharity = pvDonationsData.value;

	let evthostdata = null;
	if(gldata) {
		let evthostdata0 = pivot(gldata, `by_evt_${byHostOrAd}.buckets.$bi.{key, by_${byHostOrAd}.buckets.$bi2.$bucket}`, "$key.$bi2.$bucket");
		evthostdata = pivot(evthostdata0, 	"$evtkey.$bi2.{key, doc_count}", "$evtkey.$key.$doc_count");
	}

	// breakdown of requests by advertiser
	let adpgldata = DataStore.fetch(adpath, () => {
		let breakdowns = ['evt/vert']; // This needs to be changed to switch between ad and vert
		return ServerIO.getDataLogData({...params, breakdowns, name:'all-events',dataspace:'gl'})
			.then(res => res.cargo);
	});

	const adgldata = adpgldata.value;
	let readableAdvertStats = null;
	let prelim;

	if(adgldata) {
		//Below is fix for charity ids being saved under "ad" rather than "vert"

		// Need to put adgldata into appropriate format for formatAdvertiserData
		// Deconstructing from ES style object, so lots of nested objects
		prelim = adgldata.by_evt_vert.buckets.reduce((obj, stat) => {
			const items = stat.by_vert.buckets;
			const formattedStats = items.reduce((obj2, e) => {
				obj2[e.key] = e.doc_count;
				return obj2;
			}, {});
			obj[stat.key] = formattedStats;
			return obj;
		}, {});
		readableAdvertStats = formatAdvertiserData(prelim);//formatAdvertiserData(adEvtHostData);
	}

	return ({
		moneyFromHost,
		donationsdataByCharity,
		evthostdata,
		readableAdvertStats,
	});
};

/**HACK: puts data into human-readable form for advertiser table
 * returns breakdown of requests by adid
 * attempts to find human-readable name, just used raw Advert id if not available
 * input example: {req: {adid1: 1, adid2: 3}, open: {adid: 0, adid2: 1}}
 * output example: {adid1: 1, adid2: 3}
 * TODO: attempt to get rid of this. Can pivot be used instead?
*/
const formatAdvertiserData = (adEvtHostData) => {
	const readableAdvertStats = {};
	if(!adEvtHostData || !adEvtHostData.req) {
		console.warn('PubTrafficeReport.jsx - no useable advertiser stats found');
		return {};
	}

	const advertData = Object.keys(adEvtHostData.req)
		.map((c) => {
			if(c === "unset") return {[c]: adEvtHostData.req};
			let data = ActionMan.getDataItem({type:C.TYPES.Advert, id:c, status:C.KStatus.DRAFT, swallow: true});
			return {[c]: data.value};
		});

	//construct key-value object
	//different campaigns from the same charity will be rolled into one value
	advertData.forEach((obj, i) => {
		//should only ever be one key
		let k = Object.keys(obj)[0];
		let d = Object.values(obj)[0] || {name : `Unknown (ID: ${k})`};
		let companyName = d.name;

		if(readableAdvertStats[companyName]) readableAdvertStats[companyName]+= adEvtHostData.req[k];
		else readableAdvertStats[companyName] = adEvtHostData.req[k];
	});

	//need to resort as adding to values above has disrupted ordering of object
	const sortedKeys = Object.keys(readableAdvertStats)
		.sort((a, b) => readableAdvertStats[b] - readableAdvertStats[a]);

	const sortedAdvertStats = sortedKeys.reduce((result, k) => {
		result[k] = readableAdvertStats[k];
		return result;
	}, {});

	return sortedAdvertStats;
};

// export default PubTrafficReport;

export {
	PubTrafficReport,
	dataFetchWrapper
};
